import { ReactElement, useEffect, useState } from 'react';
import {
  Button,
  Form,
  Input,
  Checkbox,
  Select,
  FormInstance,
} from 'antd';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';
import { useIntl, FormattedMessage } from 'react-intl';
import { convertArray } from 'utils/convertArray';
import { LEGAL_BASE_URL, PRIVACY_PATH } from 'constants/application';
import { useMobileDesign } from 'hooks/useMobileDesign';
import jobAreas from 'data/containers/jobAreas';
import languages from 'data/containers/languages';
import countries from 'data/containers/countries';
import skillSets from 'data/containers/skillSets';
import CheckList from 'components/CheckList';
import Picker from 'components/Picker';

interface IntakeFormProps {
  form: FormInstance;
  loading: boolean;
  submitForm: () => void;
}

const IntakeForm = ({ form, loading, submitForm }: IntakeFormProps): ReactElement => {
  const intl = useIntl();
  const isMobile = useMobileDesign();
  const [isFormValid, setIsFormValid] = useState<boolean>(false);
  const [isPrivacyChecked, setIsPrivacyChecked] = useState<boolean>(false);
  const [showStateField, setShowStateField] = useState<boolean>(false);
  const fieldsValues = Form.useWatch([], form);

  useEffect(() => {
    form.validateFields({ validateOnly: true }).then(
      () => setIsFormValid(true),
      () => setIsFormValid(false),
    );
  }, [fieldsValues]);

  useEffect(() => {
    if (fieldsValues && fieldsValues['Currently Residing']) {
      const countriesToShowStateField = ['United States', 'Canada'];
      setShowStateField(countriesToShowStateField.includes(fieldsValues['Currently Residing']));
    } else {
      setShowStateField(false);
    }
  }, [fieldsValues]);

  const onChangePrivacyCheckbox = (e: CheckboxChangeEvent): void => {
    setIsPrivacyChecked(e.target.checked);
  };

  return (
    <Form
      form={form}
      name="intakeForm"
      layout="vertical"
      autoComplete="off"
      className="vertical-form-adjustments"
    >
      <Form.Item name="First Name" label={intl.formatMessage({ id: 'form.intake.firstName' })} rules={[{ required: true }]}>
        <Input placeholder={intl.formatMessage({ id: 'form.intake.placeholder.firstName' })} />
      </Form.Item>
      <Form.Item name="Last Name" label={intl.formatMessage({ id: 'form.intake.lastName' })} rules={[{ required: true }]}>
        <Input placeholder={intl.formatMessage({ id: 'form.intake.placeholder.lastName' })} />
      </Form.Item>
      <Form.Item name="Phone Number" label={intl.formatMessage({ id: 'form.intake.phone' })} rules={[{ required: true }]}>
        <Input placeholder={intl.formatMessage({ id: 'form.intake.placeholder.phone' })} />
      </Form.Item>
      <Form.Item name="Email" label={intl.formatMessage({ id: 'form.intake.email' })} initialValue="" rules={[{ required: true }]}>
        <Input placeholder={intl.formatMessage({ id: 'form.intake.placeholder.email' })} />
      </Form.Item>
      <Form.Item
        name="Currently Residing"
        label={intl.formatMessage({ id: 'form.intake.countryOfLivingNew' })}
        className="picker-mobile"
        rules={[{ required: true }]}
      >
        {!isMobile ? (
          <Select
            options={convertArray(countries)}
            placeholder={intl.formatMessage({ id: 'form.intake.placeholder.countryOfLivingNew' })}
          />
        ) : (
          <Picker
            name="Currently Residing"
              // @ts-ignore
            form={form}
            label={intl.formatMessage({ id: 'form.intake.countryOfLivingNew' })}
            placeholder={intl.formatMessage({ id: 'form.intake.placeholder.countryOfLivingNew' })}
            formatMessage={intl.formatMessage}
            onReject={() => {}}
            columnDataChildren={(
              convertArray(countries).map(({ value, label }) => (
                <Select.Option
                  key={value}
                  value={value}
                  title={label}
                  selectTitle={label}
                >
                  {label}
                </Select.Option>
              ))
            )}
          />
        )}
      </Form.Item>
      <Form.Item
        name="City of Residence"
        label={intl.formatMessage({ id: 'form.intake.cityOfResidence' })}
        rules={[{
          pattern: /^[a-zA-Z\- ]*$/,
          message: intl.formatMessage({ id: 'form.intake.validation.latinCharacters' }),
        }]}
      >
        <Input placeholder={intl.formatMessage({ id: 'form.intake.placeholder.cityOfResidence' })} />
      </Form.Item>
      {showStateField && (
        <Form.Item
          name="State/Province of residence"
          label={intl.formatMessage({ id: 'form.intake.stateOfResidence' })}
          rules={[{
            pattern: /^[a-zA-Z\- ]*$/,
            message: intl.formatMessage({ id: 'form.intake.validation.latinCharacters' }),
          }]}
        >
          <Input placeholder={intl.formatMessage({ id: 'form.intake.placeholder.stateOfResidence' })} />
        </Form.Item>
      )}
      <Form.Item
        name="Citizenship"
        className="max-height-group"
        label={intl.formatMessage({ id: 'form.intake.citizenshipNew' })}
        rules={[{ required: true }]}
      >
        {!isMobile ? (
          <Select
            mode="multiple"
            options={convertArray(countries)}
            placeholder={intl.formatMessage({ id: 'form.intake.placeholder.citizenshipNew' })}
          />
        ) : (
          <CheckList
            form={form}
            name="Citizenship"
            placeholder={intl.formatMessage({ id: 'form.intake.placeholder.citizenshipNew' })}
            columnDataChildren={(
              convertArray(countries).map(({ value, label }) => (
                <Select.Option
                  key={value}
                  value={value}
                  title={label}
                  selectTitle={label}
                >
                  {label}
                </Select.Option>
              ))
          )}
            items={convertArray(countries)}
          />
        )}
      </Form.Item>
      <Form.Item
        name="Languages"
        className="max-height-group"
        label={intl.formatMessage({ id: 'form.intake.languages' })}
        rules={[{ required: true }]}
      >
        {!isMobile ? (
          <Select
            mode="multiple"
            options={convertArray(languages)}
            placeholder={intl.formatMessage({ id: 'form.intake.placeholder.languages' })}
          />
        ) : (
          <CheckList
            form={form}
            name="Languages"
            placeholder={intl.formatMessage({ id: 'form.intake.placeholder.languages' })}
            columnDataChildren={(
              convertArray(languages).map(({ value, label }) => (
                <Select.Option
                  key={value}
                  value={value}
                  title={label}
                  selectTitle={label}
                >
                  {label}
                </Select.Option>
              ))
          )}
            items={convertArray(languages)}
          />
        )}
      </Form.Item>
      <Form.Item
        name="Looking for a job in the following areas"
        className="max-height-group"
        label={intl.formatMessage({ id: 'form.intake.occupationNew' })}
        rules={[{ required: true }]}
      >
        {!isMobile ? (
          <Select
            mode="multiple"
            options={convertArray(jobAreas)}
            placeholder={intl.formatMessage({ id: 'form.intake.placeholder.occupationNew' })}
          />
        ) : (
          <CheckList
            form={form}
            name="Looking for a job in the following areas"
            placeholder={intl.formatMessage({ id: 'form.intake.placeholder.occupationNew' })}
            columnDataChildren={(
              convertArray(jobAreas).map(({ value, label }) => (
                <Select.Option
                  key={value}
                  value={value}
                  title={label}
                  selectTitle={label}
                >
                  {label}
                </Select.Option>
              ))
          )}
            items={convertArray(jobAreas)}
          />
        )}
      </Form.Item>
      <Form.Item
        className="max-height-group"
        name="What are your skills"
        label={intl.formatMessage({ id: 'form.intake.professionNew' })}
        rules={[{ required: true }]}
      >
        {!isMobile ? (
          <Select
            mode="multiple"
            options={convertArray(skillSets)}
            placeholder={intl.formatMessage({ id: 'form.intake.placeholder.professionNew' })}
          />
        ) : (
          <CheckList
            form={form}
            name="What are your skills"
            placeholder={intl.formatMessage({ id: 'form.intake.placeholder.professionNew' })}
            columnDataChildren={(
                  convertArray(skillSets).map(({ value, label }) => (
                    <Select.Option
                      key={value}
                      value={value}
                      title={label}
                      selectTitle={label}
                    >
                      {label}
                    </Select.Option>
                  ))
              )}
            items={convertArray(skillSets)}
          />
        )}
      </Form.Item>
      <Form.Item
        className="max-height-group"
        label={intl.formatMessage({ id: 'form.intake.policy' })}
        rules={[{ required: true }]}
      >
        <Checkbox onChange={onChangePrivacyCheckbox}>
          <a
            href={`${LEGAL_BASE_URL}en${PRIVACY_PATH}`}
            target="_blank"
            rel="noreferrer"
            aria-label={intl.formatMessage({ id: 'form.intake.policyLink' })}
          >
            <FormattedMessage id="form.intake.policyLink" />
          </a>
        </Checkbox>
      </Form.Item>
      <Form.Item>
        <Button
          type="primary"
          loading={loading}
          className="btn btn-primary"
          htmlType="submit"
          onClick={submitForm}
          disabled={!isFormValid || !isPrivacyChecked}
        >
          <FormattedMessage id="form.intake.submit" />
        </Button>
      </Form.Item>
    </Form>
  );
};

export default IntakeForm;
